import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "../../../utils/cmsAxios";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { FiX } from "react-icons/fi";
import ConfirmModal from "../CommonComponents/ConfirmModal";

const parseServerImages = (src) => {
    // src: stateNotice 또는 data 응답 객체
    let raw =
        src?.notice_images ??
        src?.image_notices ??
        src?.images ??
        [];

    // 1) DB에서 온 JSON 문자열일 수도 있음
    if (typeof raw === "string") {
        try {
            const parsed = JSON.parse(raw);
            raw = Array.isArray(parsed) ? parsed : [];
        } catch {
            raw = [];
        }
    }

    // 2) 배열 형태 통일 후, path만 뽑기
    const normalized = (raw || [])
        .map((img) => {
            if (!img) return "";
            if (typeof img === "string") return img; // "notice/xxx.jpg"
            return img.image_path || img.path || img.url || "";
        })
        .filter(Boolean);

    return normalized;
};


const CmsNoticeCreate = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { notice_no } = useParams();
    const noticeId = notice_no ?? location.state?.notice?.notice_no ?? null;

    // 항상 관리자 가정
    const type = "admin";

    // state로 넘어온 공지(수정 모드 최적화)
    const stateNotice = location.state?.notice;

    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [isDisplay, setIsDisplay] = useState(true); // 노출 여부. UI 없으면 기본 true로 유지
    const [isPush, setIsPush] = useState(true); // 푸시 여부
    const [noticeType, setNoticeType] = useState("일반"); // 공지 유형 (일반/이벤트)

    // 🔹 신규로 선택한 이미지들
    const [imageFiles, setImageFiles] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);

    // 🔹 서버에 이미 저장된 기존 이미지들 (수정 모드에서만)
    //   - path: "/uploads/..." 같은 서버 상대 경로 또는 url
    const [existingImages, setExistingImages] = useState([]);
    //   - 기존 이미지 전부 삭제 플래그 (일부만 선택 삭제해도 서버에선 걍 다 지우고 새로 등록하는 전략)
    const [removeAllImages, setRemoveAllImages] = useState(false);

    // 단일 첨부파일(대표 이미지 등)
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState("");
    const [existingFile, setExistingFile] = useState("");
    const [removed, setRemoved] = useState(false); // 기존 파일 삭제 여부
    const MAX_SIZE_MB = 10;
    const fileInputRef = useRef(null);

    const isEdit = !!noticeId;

    const [openAlert, setOpenAlert] = useState(false);
    const [alertMsg] = useState("");
    const [openLeave, setOpenLeave] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const deleteDisabled = loading;

    // 🔹 공지 상세 불러오기 (수정 모드)
    const fetchNotice = useCallback(async () => {
        if (!isEdit) return;

        // state에 notice가 있으면 네트워크 요청 없이 초기화
        if (stateNotice) {
            setTitle(stateNotice.notice_title || "");
            setContent(stateNotice.notice_content || "");
            setIsDisplay((stateNotice?.notice_post ?? "Y") === "Y");
            setIsPush((stateNotice?.notice_push ?? "Y") === "Y");
            setNoticeType(stateNotice.notice_type || "일반");
            setExistingFile(stateNotice.notice_file || "");

            // 🔹 기존 이미지들 세팅 (JSON 문자열/배열 모두 처리)
            const normalized = parseServerImages(stateNotice);
            setExistingImages(normalized);

            return;
        }

        try {
            setLoading(true);
            setError("");
            const { data } = await axios.get(
                `${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/get/notice/${noticeId}`,
                { params: { admin: true } }
            );

            if (!data || data.notice_no == null) {
                setError("공지사항을 찾을 수 없습니다.");
                return;
            }

            setTitle(data?.notice_title || "");
            setContent(data?.notice_content || "");
            setIsDisplay((data?.notice_post ?? "Y") === "Y");
            setIsPush((data?.notice_push ?? "Y") === "Y");   // ← 이 부분도 같이 세팅
            setNoticeType(data?.notice_type || "일반");
            setExistingFile(data?.notice_file || "");

            // 🔹 기존 이미지들 세팅
            const normalized = parseServerImages(data);
            setExistingImages(normalized);
        } catch (e) {
            setError("공지사항을 불러오는 데 실패했습니다.");
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [isEdit, noticeId, stateNotice]);


    useEffect(() => {
        fetchNotice();
    }, [fetchNotice]);

    // 이미지 최대 3장
    const MAX_IMAGES = 3;


    // 이미지 경로 잡기
    // 컴포넌트 함수 안쪽, useState/useEffect 위나 아래 아무데나
    const buildUploadUrl = (path) => {
        if (!path) return "";
        if (path.startsWith("http")) return path;

        // 도메인은 환경변수 있으면 그거, 없으면 현재 origin 사용
        const origin =
            process.env.REACT_APP_UPLOAD_BASE_URL ||
            `${window.location.protocol}//${window.location.host}`;

        // 앞에 슬래시/ uploads 중복 제거
        const cleaned = path.replace(/^\/+/, ""); // 맨 앞 / 지우기
        // 이미 uploads/로 시작하면 그대로, 아니면 /uploads 붙이기
        const fullPath = cleaned.startsWith("uploads/")
            ? cleaned
            : `uploads/${cleaned}`;

        return `${origin}/${fullPath}`;
    };



    // 🔹 신규 이미지 선택
    const handleImagesChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        const next = [...imageFiles];
        const nextPreviews = [...imagePreviews];

        for (const f of files) {
            if (next.length >= MAX_IMAGES) break;
            if (!f.type.startsWith("image/")) {
                alert("이미지 파일만 등록 가능합니다.");
                continue;
            }
            if (f.size > MAX_SIZE_MB * 1024 * 1024) {
                alert(`최대 ${MAX_SIZE_MB}MB까지 업로드 가능합니다.`);
                continue;
            }
            next.push(f);
            nextPreviews.push(URL.createObjectURL(f));
        }

        setImageFiles(next);
        setImagePreviews(nextPreviews);
        e.target.value = "";
    };

    // 🔹 신규 이미지 삭제
    const handleImageRemove = (index) => {
        const next = [...imageFiles];
        const nextPreviews = [...imagePreviews];

        const removedPreview = nextPreviews[index];

        next.splice(index, 1);
        nextPreviews.splice(index, 1);

        setImageFiles(next);
        setImagePreviews(nextPreviews);

        if (removedPreview) {
            URL.revokeObjectURL(removedPreview);
        }
    };

    // 🔹 기존 서버 이미지 개별 삭제
    //    → 백엔드에선 부분 삭제를 지원 안 해도,
    //      프론트 기준으로 "기존 건 다 지우고 새로 올리는" 전략으로 처리
    const handleExistingImageRemove = (index) => {
        setExistingImages((prev) => prev.filter((_, i) => i !== index));
        // 기존 이미지를 하나라도 삭제했다 = 서버에선 전체 기존 이미지를 지우고,
        // 새로 올린 imageFiles만 남기도록 요청
        setRemoveAllImages(true);
    };

    // 🔹 단일 첨부파일 변경
    const handleFileChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;

        if (f.size > MAX_SIZE_MB * 1024 * 1024) {
            alert(`최대 ${MAX_SIZE_MB}MB까지 업로드 가능합니다.`);
            e.target.value = "";
            return;
        }
        setFile(f);
        setRemoved(false);
        setExistingFile("");
    };

    // 🔹 단일 첨부파일 미리보기
    useEffect(() => {
        if (!file) {
            setPreviewUrl("");
            return;
        }
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    // 🔹 단일 첨부파일 제거
    const handleFileClear = () => {
        setFile(null);
        setPreviewUrl("");
        setRemoved(true);
        setExistingFile("");
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // 🔹 저장/수정
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!title.trim() || !content.trim()) {
            setError("제목과 내용을 모두 입력해주세요.");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const form = new FormData();
            form.append("notice_title", title.trim());
            form.append("notice_content", content.trim());
            form.append("notice_post", isDisplay ? "Y" : "N");
            form.append("notice_push", isPush ? "Y" : "N");
            form.append("notice_type", noticeType);

            // 🔹 수정 모드일 때만: 남겨둘 기존 이미지 목록
            if (isEdit) {
                form.append("existing_images", JSON.stringify(existingImages));
            }

            // 🔹 새로 선택한 이미지들 1번만 추가
            imageFiles.forEach((img) => {
                form.append("notice_images", img);
            });

            // 🔹 단일 첨부파일
            if (file) {
                // 새 파일로 교체
                form.append("notice_file", file);
            } else if (isEdit && removed) {
                // 기존 파일 삭제만 요청
                form.append("remove_file", "true"); // FastAPI bool Form으로 잘 파싱됨
            }

            const url = isEdit
                ? `${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/edit/notice/${noticeId}`
                : `${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/create/notice`;

            await axios.post(url, form, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            navigate("/cms/notice", { state: { type }, replace: true });
        } catch (e) {
            console.error("공지 저장 실패:", e);
            setError(
                isEdit
                    ? "공지사항 수정에 실패했습니다."
                    : "공지사항 등록에 실패했습니다."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleGoToList = () => {
        navigate("/cms/notice", { state: { type }, replace: true });
    };

    const handleDeleteClick = async () => {
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/delete/notice/${notice_no}`
            );
            if (res.data?.success) {
                navigate("/cms/notice", { state: { type }, replace: true });
            } else {
                alert("삭제에 실패했습니다.");
            }
        } catch (e) {
            console.error(e);
            alert("서버 오류로 삭제에 실패했습니다.");
        }
    };

    return (
        <div className="min-h-screen bg-neutral-50">
            <div className="mx-auto max-w-[1280px] bg-white">
                <div className="flex">
                    <div className="flex-1 min-w-0 p-4 bg-neutral-50/70">
                        <main className="pt-12 flex flex-col p-4 overflow-x-hidden w-full">
                            <section className="p-4">
                                {/* 헤더 */}
                                <div className="mb-3 flex items-center justify-between">
                                    <h2 className="text-xl font-semibold mb-4">
                                        {isEdit ? "공지사항 > 상세(수정)" : "공지사항 > 등록"}
                                    </h2>
                                </div>

                                {/* 에러 */}
                                {error && (
                                    <div className="mb-3 rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">
                                        {error}
                                    </div>
                                )}

                                {/* 폼 */}
                                <form onSubmit={handleSubmit} className="space-y-4 max-w-full">
                                    <h3 className="text-t3 font-semibold text-neutral-900">
                                        공지사항 내용
                                    </h3>
                                    <div className="rounded-xl border-neutral-200 bg-white overflow-hidden p-3">
                                        <div className="divide-y divide-neutral-200">
                                            {/* 노출 */}
                                            <div className="flex">
                                                <div className="w-40 shrink-0 bg-neutral-50 px-4 py-2 text-sm font-medium text-neutral-700">
                                                    노출
                                                </div>
                                                <div className="flex-1 px-4 py-2">
                                                    <div className="flex items-center gap-6">
                                                        <label className="inline-flex items-center gap-2">
                                                            <input
                                                                type="radio"
                                                                name="display"
                                                                checked={isDisplay === true}
                                                                onChange={() => setIsDisplay(true)}
                                                            />
                                                            <span>Yes</span>
                                                        </label>
                                                        <label className="inline-flex items-center gap-2">
                                                            <input
                                                                type="radio"
                                                                name="display"
                                                                checked={isDisplay === false}
                                                                onChange={() => setIsDisplay(false)}
                                                            />
                                                            <span>No</span>
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* 푸시 */}
                                            <div className="flex">
                                                <div className="w-40 shrink-0 bg-neutral-50 px-4 py-2 text-sm font-medium text-neutral-700">
                                                    푸시
                                                </div>
                                                <div className="flex-1 px-4 py-2">
                                                    <div className="flex items-center gap-6">
                                                        <label className="inline-flex items-center gap-2">
                                                            <input
                                                                type="radio"
                                                                name="push"
                                                                checked={isPush === true}
                                                                onChange={() => setIsPush(true)}
                                                            />
                                                            <span>Yes</span>
                                                        </label>
                                                        <label className="inline-flex items-center gap-2">
                                                            <input
                                                                type="radio"
                                                                name="push"
                                                                checked={isPush === false}
                                                                onChange={() => setIsPush(false)}
                                                            />
                                                            <span>No</span>
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* 유형 */}
                                            <div className="flex">
                                                <div className="w-40 shrink-0 bg-neutral-50 px-4 py-2 text-sm font-medium text-neutral-700">
                                                    유형
                                                </div>
                                                <div className="flex-1 px-4 py-2">
                                                    <div className="flex items-center gap-6">
                                                        <label className="inline-flex items-center gap-2">
                                                            <input
                                                                type="radio"
                                                                name="notice_type"
                                                                value="일반"
                                                                checked={noticeType === "일반"}
                                                                onChange={() => setNoticeType("일반")}
                                                            />
                                                            <span>일반</span>
                                                        </label>
                                                        <label className="inline-flex items-center gap-2">
                                                            <input
                                                                type="radio"
                                                                name="notice_type"
                                                                value="이벤트"
                                                                checked={noticeType === "이벤트"}
                                                                onChange={() => setNoticeType("이벤트")}
                                                            />
                                                            <span>이벤트</span>
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* 제목 */}
                                            <div className="flex">
                                                <div className="w-40 shrink-0 bg-neutral-50 px-4 py-2 text-sm font-medium text-neutral-700">
                                                    제목
                                                </div>
                                                <div className="flex-1 px-4 py-2">
                                                    <input
                                                        id="title"
                                                        type="text"
                                                        value={title}
                                                        onChange={(e) => setTitle(e.target.value)}
                                                        className="block w-full rounded-lg border px-3 py-2 text-sm"
                                                        placeholder="공지사항 제목을 입력하세요"
                                                        maxLength={100}
                                                    />
                                                </div>
                                            </div>

                                            {/* 내용 */}
                                            <div className="flex">
                                                <div className="w-40 shrink-0 bg-neutral-50 px-4 py-2 text-sm font-medium text-neutral-700">
                                                    내용
                                                </div>
                                                <div className="flex-1 px-4 py-2">
                                                    <textarea
                                                        id="content"
                                                        value={content}
                                                        onChange={(e) => setContent(e.target.value)}
                                                        className="block w-full rounded-lg border px-3 py-2 text-sm"
                                                        rows={10}
                                                        placeholder="공지사항 내용을 입력하세요"
                                                        maxLength={2000}
                                                    />
                                                </div>
                                            </div>

                                            {/* 첨부 이미지 */}
                                            <div className="flex">
                                                <div className="w-40 shrink-0 bg-neutral-50 px-4 py-2 text-sm font-medium text-neutral-700">
                                                    첨부 이미지
                                                </div>
                                                <div className="flex-1 px-4 py-2">
                                                    <div className="flex flex-wrap gap-3">
                                                        {/* 기존 서버 이미지들 */}
                                                        {existingImages.map((path, idx) => (
                                                            <div
                                                                key={`exist-${idx}`}
                                                                className="relative w-[80px] h-[80px] rounded-lg border border-dashed border-neutral-300 overflow-hidden flex items-center justify-center bg-neutral-50"
                                                            >
                                                                <img
                                                                    src={buildUploadUrl(path)}   // 🔹 여기만 변경
                                                                    alt={`기존 이미지 ${idx + 1}`}
                                                                    className="w-full h-full object-cover"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleExistingImageRemove(idx)}
                                                                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-black/70 text-white text-[11px] flex items-center justify-center"
                                                                >
                                                                    ✕
                                                                </button>
                                                                <span className="absolute bottom-1 right-1 text-[11px] text-white bg-black/50 rounded px-1">
                                                                    기존
                                                                </span>
                                                            </div>
                                                        ))}

                                                        {/* 새로 선택한 이미지들 */}
                                                        {imagePreviews.map((src, idx) => (
                                                            <div
                                                                key={`new-${idx}`}
                                                                className="relative w-[80px] h-[80px] rounded-lg border border-dashed border-neutral-300 overflow-hidden flex items-center justify-center bg-neutral-50"
                                                            >
                                                                <img
                                                                    src={src}
                                                                    alt={`첨부 이미지 ${idx + 1}`}
                                                                    className="w-full h-full object-cover"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleImageRemove(idx)}
                                                                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-black/70 text-white text-[11px] flex items-center justify-center"
                                                                >
                                                                    ✕
                                                                </button>
                                                                <span className="absolute bottom-1 right-1 text-[11px] text-white bg-black/50 rounded px-1">
                                                                    {idx + 1}
                                                                </span>
                                                            </div>
                                                        ))}

                                                        {/* 추가 버튼 */}
                                                        {imageFiles.length < MAX_IMAGES && (
                                                            <label className="w-[80px] h-[80px] rounded-lg border border-dashed border-neutral-300 flex flex-col items-center justify-center text-xs text-neutral-400 cursor-pointer bg-neutral-50 hover:bg-neutral-100">
                                                                <span className="text-lg mb-1">+</span>
                                                                <span>이미지 추가</span>
                                                                <input
                                                                    type="file"
                                                                    accept="image/*"
                                                                    multiple
                                                                    onChange={handleImagesChange}
                                                                    className="hidden"
                                                                />
                                                            </label>
                                                        )}
                                                    </div>
                                                    <p className="mt-1 text-xs text-neutral-500">
                                                        (최대 {MAX_IMAGES}개 {MAX_SIZE_MB}MB 등록 가능)
                                                    </p>
                                                </div>
                                            </div>


                                            {/* 첨부파일 (단일) */}
                                            <div className="flex">
                                                <div className="w-40 shrink-0 bg-neutral-50 px-4 py-2 text-sm font-medium text-neutral-700">
                                                    첨부파일
                                                </div>
                                                <div className="flex-1 px-4 py-2">
                                                    <div className="mt-1 flex items-center gap-2">
                                                        <input
                                                            type="text"
                                                            readOnly
                                                            value={
                                                                file
                                                                    ? file.name
                                                                    : removed
                                                                        ? "선택된 파일 없음"
                                                                        : existingFile
                                                                            ? existingFile.split("/").pop()
                                                                            : "선택된 파일 없음"
                                                            }
                                                            className="flex-1 rounded-lg border px-3 py-2 text-sm text-neutral-700"
                                                        />
                                                        {(file || (existingFile && !removed)) && (
                                                            <button
                                                                type="button"
                                                                onClick={handleFileClear}
                                                                className="inline-flex items-center justify-center rounded-lg border px-2 py-2 text-sm hover:bg-neutral-50"
                                                                aria-label="첨부파일 제거"
                                                            >
                                                                <FiX className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                        <label className="relative inline-flex items-center justify-center rounded-lg bg-neutral-200 px-3 py-2 text-sm cursor-pointer hover:bg-neutral-300">
                                                            찾아보기
                                                            <input
                                                                type="file"
                                                                // accept="image/*" // 이제 다양한 타입 허용
                                                                onChange={handleFileChange}
                                                                ref={fileInputRef}
                                                                className="absolute inset-0 opacity-0 cursor-pointer"
                                                            />
                                                        </label>
                                                    </div>
                                                    <p className="mt-1 text-xs text-neutral-500">
                                                        (최대 {MAX_SIZE_MB}MB 등록 가능)
                                                    </p>

                                                    {(previewUrl || (existingFile && !file && !removed)) && (
                                                        <div className="pt-2">
                                                            {/* 새로 올린 파일이 이미지인지 체크 */}
                                                            {file?.type?.startsWith("image/") ||
                                                                /\.(png|jpe?g|gif|webp)$/i.test(existingFile || "") ? (
                                                                <img
                                                                    src={previewUrl || buildUploadUrl(existingFile)}
                                                                    alt="첨부 미리보기"
                                                                    className="max-h-40 rounded border"
                                                                />
                                                            ) : (
                                                                <a
                                                                    href={buildUploadUrl(existingFile)}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-sm text-blue-600 underline"
                                                                >
                                                                    첨부파일 열기
                                                                </a>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                        </div>
                                    </div>

                                    {/* 하단 버튼 영역 */}
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex flex-1 justify-center gap-2">
                                            <button
                                                type="submit"
                                                disabled={loading}
                                                className="rounded-lg bg-violet-300 text-violet-900 px-4 py-2 text-sm hover:bg-violet-500 disabled:opacity-50"
                                            >
                                                {loading
                                                    ? isEdit
                                                        ? "수정 중..."
                                                        : "등록 중..."
                                                    : isEdit
                                                        ? "저장"
                                                        : "등록"}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleGoToList}
                                                className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
                                            >
                                                이전
                                            </button>
                                        </div>
                                        <button
                                            type="button"
                                            disabled={deleteDisabled}
                                            onClick={() => {
                                                if (!deleteDisabled) setOpenDelete(true);
                                            }}
                                            className={`rounded-lg px-4 py-2 text-sm border ${deleteDisabled
                                                ? "border-neutral-200 text-neutral-300 cursor-not-allowed"
                                                : "border-red-300 text-red-600 hover:bg-red-50"
                                                }`}
                                        >
                                            삭제
                                        </button>
                                    </div>
                                </form>

                                {/* 알림 */}
                                <ConfirmModal
                                    open={openAlert}
                                    title=""
                                    message={alertMsg || "처리가 완료되었습니다."}
                                    onClose={() => {
                                        setOpenAlert(false);
                                        if (
                                            alertMsg &&
                                            (alertMsg.includes("등록") ||
                                                alertMsg.includes("수정") ||
                                                alertMsg.includes("삭제"))
                                        ) {
                                            handleGoToList();
                                        }
                                    }}
                                    buttons={[
                                        {
                                            label: "확인",
                                            intent: "primary",
                                            autoFocus: true,
                                            onClick: () => {
                                                setOpenAlert(false);
                                                if (
                                                    alertMsg &&
                                                    (alertMsg.includes("등록") ||
                                                        alertMsg.includes("수정") ||
                                                        alertMsg.includes("삭제"))
                                                ) {
                                                    handleGoToList();
                                                }
                                            },
                                        },
                                    ]}
                                />

                                {/* 이탈 확인 */}
                                <ConfirmModal
                                    open={openLeave}
                                    title="이전"
                                    message="변경사항을 저장하지 않고 이전페이지로 이동하시겠습니까?"
                                    onClose={() => setOpenLeave(false)}
                                    buttons={[
                                        {
                                            label: "저장하지 않고 이동",
                                            onClick: () => {
                                                setOpenLeave(false);
                                                handleGoToList();
                                            },
                                        },
                                        {
                                            label: "현 페이지에 머무르기",
                                            intent: "primary",
                                            autoFocus: true,
                                            onClick: () => setOpenLeave(false),
                                        },
                                    ]}
                                />
                                <ConfirmModal
                                    open={openDelete}
                                    title="삭제"
                                    message="해당 게시물을 삭제하시겠습니까?"
                                    onClose={() => setOpenDelete(false)}
                                    buttons={[
                                        {
                                            label: "삭제",
                                            intent: "primary",
                                            onClick: () => handleDeleteClick(),
                                        },
                                        { label: "취소", onClick: () => setOpenDelete(false) },
                                    ]}
                                />
                            </section>
                        </main>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CmsNoticeCreate;
